import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Perspective Flip modal, shown after answering a Head-vs-Heart item (question_id starts with "hh_").
 * Props: open, onClose, assessmentId, questionId, prompt (the HH scenario prompt).
 */
export default function PerspectiveFlip({ open, onClose, assessmentId, questionId, prompt }) {
  const [other, setOther] = useState("");
  const [info, setInfo] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!other.trim() || !info.trim()) { toast.error("Fill both prompts (brief is fine)"); return; }
    setSaving(true);
    try {
      await api.post("/perspective-flip/respond", {
        assessment_id: assessmentId, question_id: questionId,
        other_perspective: other, info_that_would_change: info,
      });
      toast.success("Perspective recorded");
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="perspective-flip-modal">
      <div className="w-full max-w-lg rounded-2xl bg-[#131E36] border border-[#E0A96D]/25 p-6 sm:p-7 fade-up">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#A855F7] mb-2">Perspective Flip</div>
        <div className="font-serif text-2xl mb-3">Two quick follow-ups</div>
        <p className="text-sm text-slate-400 mb-4 italic">"{prompt}"</p>

        <label className="block text-sm text-slate-300 mb-1">What do you think the <strong>other person</strong> believes is happening?</label>
        <textarea rows={3} value={other} onChange={e=>setOther(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 focus:border-[#E0A96D] outline-none text-sm mb-4"
          data-testid="pf-other" placeholder="Their likely interpretation…" />

        <label className="block text-sm text-slate-300 mb-1">Which <strong>one piece of information</strong> would most change your interpretation?</label>
        <textarea rows={3} value={info} onChange={e=>setInfo(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 focus:border-[#E0A96D] outline-none text-sm"
          data-testid="pf-info" placeholder="If I knew that…" />

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-400 hover:text-slate-200 text-sm" data-testid="pf-skip">Skip</button>
          <button onClick={submit} disabled={saving}
            className="px-5 py-2 rounded-lg bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60 text-sm"
            data-testid="pf-save">
            {saving?"Saving…":"Save & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
